import { Typography, Box, Paper, Grid } from '@mui/material';
import { BooleanInput, TextInput } from 'react-admin';
import { useWatch, useFormContext } from 'react-hook-form';
import { useEffect } from 'react';

function CsvConfigEditor() {
  const { setValue } = useFormContext();
  // Watch form values for conditional rendering
  const runRequests = useWatch({ name: 'config.run.requests' });
  const runEmployees = useWatch({ name: 'config.run.employees' });
  const runPositions = useWatch({ name: 'config.run.positions' });
  const runDepartments = useWatch({ name: 'config.run.departments' });
  const runSubsidiaries = useWatch({ name: 'config.run.subsidiaries' });

  // Clear converter values when run configuration is disabled
  useEffect(() => {
    if (!runRequests) {
      setValue('config.converters.requests', '');
    }
  }, [runRequests, setValue]);

  useEffect(() => {
    if (!runEmployees) {
      setValue('config.converters.employees', '');
    }
  }, [runEmployees, setValue]);

  useEffect(() => {
    if (!runPositions) {
      setValue('config.converters.positions', '');
    }
  }, [runPositions, setValue]);

  useEffect(() => {
    if (!runDepartments) {
      setValue('config.converters.departments', '');
    }
  }, [runDepartments, setValue]);

  useEffect(() => {
    if (!runSubsidiaries) {
      setValue('config.converters.subsidiaries', '');
    }
  }, [runSubsidiaries, setValue]);

  // Clear and disable request-dependent fields when run.requests is disabled
  useEffect(() => {
    if (!runRequests) {
      setValue('config.filterProcessedRequests', false);
      setValue('config.allowEndlessRequests', false);
    }
  }, [runRequests, setValue]);

  // Validation function for converters
  const validateConverter = (converterType: string) => (value: any) => {
    const runConfigMap: { [key: string]: boolean } = {
      requests: runRequests,
      employees: runEmployees,
      positions: runPositions,
      departments: runDepartments,
      subsidiaries: runSubsidiaries
    };

    const isRunEnabled = runConfigMap[converterType];

    if (isRunEnabled && (!value || value.toString().trim() === '')) {
      return `${converterType.charAt(0).toUpperCase() + converterType.slice(1)} converter is required when run configuration is enabled`;
    }
    return undefined;
  };

  // Validation function for separator - always required
  const validateSeparator = (value: any) => {
    if (!value || value.toString().trim() === '') {
      return 'CSV separator is required';
    }
    return undefined;
  };

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Run Configuration Section */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Run Configuration
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <BooleanInput source="config.run.requests" label="Run Requests" />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <BooleanInput source="config.run.employees" label="Run Employees" />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <BooleanInput source="config.run.positions" label="Run Positions" />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <BooleanInput source="config.run.departments" label="Run Departments" />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <BooleanInput source="config.run.subsidiaries" label="Run Subsidiaries" />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Separator Configuration */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              CSV Separator
            </Typography>
            <TextInput source="config.separator" label="Separator" placeholder="Enter CSV separator (e.g., ',', ';', '|')" helperText="Character used to separate CSV fields" validate={validateSeparator} fullWidth />
          </Paper>
        </Grid>

        {/* Converters Configuration */}
        {(runRequests || runEmployees || runPositions || runDepartments || runSubsidiaries) && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Converters Configuration
              </Typography>
              <Grid container spacing={2}>
                {runRequests && (
                  <Grid item xs={12} md={6}>
                    <TextInput source="config.converters.requests" label="Requests Converter" placeholder="Enter requests converter configuration" validate={validateConverter('requests')} fullWidth />
                  </Grid>
                )}
                {runEmployees && (
                  <Grid item xs={12} md={6}>
                    <TextInput source="config.converters.employees" label="Employees Converter" placeholder="Enter employees converter configuration" validate={validateConverter('employees')} fullWidth />
                  </Grid>
                )}
                {runPositions && (
                  <Grid item xs={12} md={6}>
                    <TextInput source="config.converters.positions" label="Positions Converter" placeholder="Enter positions converter configuration" validate={validateConverter('positions')} fullWidth />
                  </Grid>
                )}
                {runDepartments && (
                  <Grid item xs={12} md={6}>
                    <TextInput source="config.converters.departments" label="Departments Converter" placeholder="Enter departments converter configuration" validate={validateConverter('departments')} fullWidth />
                  </Grid>
                )}
                {runSubsidiaries && (
                  <Grid item xs={12} md={6}>
                    <TextInput source="config.converters.subsidiaries" label="Subsidiaries Converter" placeholder="Enter subsidiaries converter configuration" validate={validateConverter('subsidiaries')} fullWidth />
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>
        )}

        {/* Additional Options */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Additional Options
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <BooleanInput source="config.populateSchedules" label="Populate Schedules" />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <BooleanInput source="config.populateSupervisors" label="Populate Supervisors" />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <BooleanInput source="config.allowEndlessRequests" label="Allow Endless Requests" disabled={!runRequests} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <BooleanInput source="config.unchangeableSchedules" label="Unchangeable Schedules" />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <BooleanInput source="config.filterProcessedRequests" label="Filter Processed Requests" disabled={!runRequests} />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default CsvConfigEditor;
